import { useState } from "react";
import { X, FileText, Image, ExternalLink, Download } from "lucide-react";
import { API_BASE } from "../../api/client";
import type { Receipt, Category } from "../../types";
import { CategoryBadge } from "../categories/CategoryBadge";

interface Props {
  receipt: Receipt;
  categories: Category[];
  onUpdate: (id: string, updates: Partial<Receipt>) => Promise<Receipt>;
  onDelete: (id: string) => Promise<void>;
  onClose: () => void;
}

const iconLinkStyle: React.CSSProperties = {
  color: "#94a3b8", display: "flex", alignItems: "center", gap: "0.35rem",
  fontSize: "0.85rem", textDecoration: "none", background: "none", border: "none",
  cursor: "pointer", padding: 0,
};

function FileViewerModal({ url, filename, fileType, onClose }: { url: string; filename: string; fileType: string; onClose: () => void }) {
  const isPdf = fileType === "pdf";
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 200, display: "flex", flexDirection: "column" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", padding: "0.75rem 1rem", gap: "1rem", flexShrink: 0 }}>
        <a href={`${url}&download=1`} download={filename} style={iconLinkStyle}>
          <Download size={15} /> Download
        </a>
        <a href={url} target="_blank" rel="noreferrer" style={iconLinkStyle}>
          <ExternalLink size={15} /> Open in new tab
        </a>
        <button onClick={onClose} style={{ ...iconLinkStyle, padding: "0.25rem" }}>
          <X size={22} />
        </button>
      </div>
      <div style={{ flex: 1, overflow: "hidden", display: "flex", justifyContent: "center", alignItems: "center", padding: "0 1rem 1rem" }}>
        {isPdf ? (
          <iframe
            src={url}
            style={{ width: "100%", height: "100%", border: "none", borderRadius: "6px", background: "#fff" }}
            title="Receipt PDF"
          />
        ) : (
          <img
            src={url}
            alt="Receipt"
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: "6px" }}
          />
        )}
      </div>
    </div>
  );
}

export function ReceiptDetail({ receipt, categories, onUpdate, onDelete, onClose }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(receipt);
  const [saving, setSaving] = useState(false);
  const [viewingFile, setViewingFile] = useState(false);
  const category = categories.find(c => c.id === receipt.category_id);
  const fileUrl = `${API_BASE}/api/receipts/${receipt.id}/file?token=${localStorage.getItem("access_token") ?? ""}`;

  async function handleSave() {
    setSaving(true);
    try {
      await onUpdate(draft.id, {
        merchant: draft.merchant ?? undefined,
        receipt_date: draft.receipt_date ?? undefined,
        amount: draft.amount ?? undefined,
        vat_amount: draft.vat_amount ?? undefined,
        category_id: draft.category_id ?? undefined,
        notes: draft.notes ?? undefined,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  const overlayStyle: React.CSSProperties = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", justifyContent: "flex-end", zIndex: 100,
  };
  const drawerStyle: React.CSSProperties = {
    background: "#1e293b", width: "100%", maxWidth: "420px",
    height: "100%", overflowY: "auto", padding: "1.5rem", boxSizing: "border-box",
  };
  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "0.5rem 0.75rem", background: "#0f172a",
    border: "1px solid #334155", borderRadius: "4px", color: "#f1f5f9", boxSizing: "border-box",
  };

  return (
    <>
    {viewingFile && <FileViewerModal url={fileUrl} filename={receipt.filename} fileType={receipt.file_type} onClose={() => setViewingFile(false)} />}
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={drawerStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h2 style={{ color: "#f1f5f9", margin: 0 }}>Receipt</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", display: "flex" }}><X size={22} /></button>
        </div>

        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
          <button onClick={() => setViewingFile(true)}
            style={{ flex: 1, background: "#0f172a", borderRadius: "6px", padding: "0.75rem 0.5rem", textAlign: "center", color: "#3b82f6", border: "1px solid #1e3a5f", cursor: "pointer", fontSize: "0.85rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem" }}>
            {receipt.file_type === "pdf" ? <FileText size={15} /> : <Image size={15} />} View
          </button>
          <a href={`${fileUrl}&download=1`} download={receipt.filename}
            style={{ flex: 1, background: "#0f172a", borderRadius: "6px", padding: "0.75rem 0.5rem", textAlign: "center", color: "#3b82f6", border: "1px solid #1e3a5f", cursor: "pointer", fontSize: "0.85rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem", textDecoration: "none" }}>
            <Download size={15} /> Download
          </a>
        </div>

        {!editing ? (
          <div>
            <Row label="Merchant" value={receipt.merchant || "—"} />
            <Row label="Date" value={receipt.receipt_date ? new Date(receipt.receipt_date).toLocaleDateString("en-GB") : "—"} />
            <Row label="Amount" value={receipt.amount ? `£${parseFloat(receipt.amount).toFixed(2)}` : "—"} />
            <Row label="VAT" value={receipt.vat_amount ? `£${parseFloat(receipt.vat_amount).toFixed(2)}` : "—"} />
            <div style={{ marginBottom: "1rem" }}>
              <span style={{ color: "#64748b", fontSize: "0.8rem" }}>Category</span>
              <div style={{ marginTop: "0.25rem" }}><CategoryBadge category={category} /></div>
            </div>
            <Row label="Notes" value={receipt.notes || "—"} />
            <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem" }}>
              <button onClick={() => setEditing(true)}
                style={{ flex: 1, padding: "0.5rem", background: "#334155", color: "#f1f5f9", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                Edit
              </button>
              <button onClick={() => { onDelete(receipt.id); onClose(); }}
                style={{ flex: 1, padding: "0.5rem", background: "#7f1d1d", color: "#fca5a5", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                Delete
              </button>
            </div>
          </div>
        ) : (
          <div>
            <Field label="Merchant" value={draft.merchant ?? ""} onChange={v => setDraft({ ...draft, merchant: v })} inputStyle={inputStyle} />
            <Field label="Date" type="date" value={draft.receipt_date ?? ""} onChange={v => setDraft({ ...draft, receipt_date: v })} inputStyle={inputStyle} />
            <Field label="Amount (£)" type="number" value={draft.amount ?? ""} onChange={v => setDraft({ ...draft, amount: v })} inputStyle={inputStyle} />
            <Field label="VAT (£)" type="number" value={draft.vat_amount ?? ""} onChange={v => setDraft({ ...draft, vat_amount: v })} inputStyle={inputStyle} />
            <div style={{ marginBottom: "0.75rem" }}>
              <label style={{ color: "#94a3b8", fontSize: "0.8rem", display: "block", marginBottom: "0.25rem" }}>Category</label>
              <select style={{ ...inputStyle, fontSize: "1rem" }} value={draft.category_id ?? ""}
                onChange={e => setDraft({ ...draft, category_id: e.target.value || null })}>
                <option value="">Uncategorised</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ color: "#94a3b8", fontSize: "0.8rem", display: "block", marginBottom: "0.25rem" }}>Notes</label>
              <textarea style={{ ...inputStyle, height: "60px", resize: "vertical" }} value={draft.notes ?? ""}
                onChange={e => setDraft({ ...draft, notes: e.target.value })} />
            </div>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button onClick={handleSave} disabled={saving}
                style={{ flex: 1, padding: "0.5rem", background: "#1d4ed8", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={() => { setDraft(receipt); setEditing(false); }}
                style={{ flex: 1, padding: "0.5rem", background: "#334155", color: "#94a3b8", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginBottom: "1rem" }}>
      <span style={{ color: "#64748b", fontSize: "0.8rem" }}>{label}</span>
      <div style={{ color: "#f1f5f9", marginTop: "0.2rem" }}>{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", inputStyle }: { label: string; value: string; onChange: (v: string) => void; type?: string; inputStyle: React.CSSProperties }) {
  return (
    <div style={{ marginBottom: "0.75rem" }}>
      <label style={{ color: "#94a3b8", fontSize: "0.8rem", display: "block", marginBottom: "0.25rem" }}>{label}</label>
      <input style={inputStyle} type={type} value={value} onChange={e => onChange(e.target.value)} />
    </div>
  );
}
